// Pure helpers shared by the Receivers page and the receiver detail tabs for
// reasoning about what a receiver runs and what it feeds. All inputs are the
// backend's `getReceiversMetadata` overlay (`meta`) and `Receiver` rows.

// A receiver is "running aviotrack" (so Performance / Remote Config work) when
// it has sent a STATS payload within this window.
export const AVIOTRACK_FRESH_WINDOW_MS = 5 * 60 * 1000;

// A traffic type is shown solid when seen within this window, muted otherwise.
export const TRAFFIC_FRESH_WINDOW_MS = 24 * 60 * 60 * 1000;

/** True when the receiver has ever reported as the Aviotrack receiver software. */
export function hasEverSentStats(meta) {
  return !!(meta && meta.software === 'aviotrack' && meta.lastStatsAtMs);
}

/** True when the Aviotrack receiver software is running right now (fresh STATS). */
export function isAviotrackActive(meta) {
  if (!hasEverSentStats(meta)) return false;
  return (Date.now() - meta.lastStatsAtMs) <= AVIOTRACK_FRESH_WINDOW_MS;
}

/**
 * Software label for the list. The STATS fingerprint wins; otherwise the
 * software the owner declared on the receiver's setup page (readsb, dump1090,
 * aviotrack-linux …). `declared: true` marks the latter so it can be styled
 * as "what they told us" rather than "what we observed".
 * @returns {{label: string, declared: boolean}|null}
 */
export function softwareLabel(meta, receiver) {
  if (hasEverSentStats(meta)) return { label: meta.software, declared: false };
  const declared = receiver?.softwareVersion;
  if (declared && String(declared).trim()) return { label: String(declared).trim(), declared: true };
  return null;
}

/** SDR / device type: live STATS value, else the hardware chosen on the setup page. */
export function deviceTypeLabel(meta, receiver) {
  if (meta?.deviceType) return meta.deviceType;
  const declared = receiver?.receiverType;
  return declared && String(declared).trim() ? String(declared).trim() : null;
}

/**
 * Traffic types a receiver feeds, newest first, each flagged fresh (< 24 h).
 * Merges the persisted `receiver.trafficTypes` (survives restarts, covers
 * offline receivers) with the live websocket rates so a receiver feeding right
 * now shows solid even before the throttled DB stamp lands.
 * @param {object} receiver  row from getReceivers / getAllReceivers
 * @param {object} [liveStats] entry from receiverStatistics (beastMsgRate, acarsMsgRate)
 * @param {number} [now]
 * @returns {Array<{type: string, fresh: boolean, lastSeenAt: string|null}>}
 */
export function trafficTypes(receiver, liveStats, now = Date.now()) {
  const out = new Map();
  (receiver?.trafficTypes || []).forEach(t => {
    if (!t || !t.type) return;
    const seenMs = t.lastSeenAt ? parseUtc(t.lastSeenAt) : null;
    const fresh = seenMs !== null && (now - seenMs) <= TRAFFIC_FRESH_WINDOW_MS;
    out.set(t.type, { type: t.type, fresh, lastSeenAt: t.lastSeenAt || null });
  });
  if (liveStats) {
    if (liveStats.beastMsgRate > 0) markLive(out, 'ADS-B');
    if (liveStats.acarsMsgRate > 0 && !out.has('VDL2')) markLive(out, 'ACARS');
  }
  return Array.from(out.values());
}

function markLive(map, type) {
  const existing = map.get(type);
  map.set(type, { type, fresh: true, lastSeenAt: existing?.lastSeenAt || null });
}

// The server emits LocalDateTime with no zone; it runs in UTC. Mirror
// utils/dateTime.js rather than importing it so this module stays free of
// rendering concerns.
function parseUtc(value) {
  if (!value) return null;
  const s = String(value);
  const iso = /[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s : `${s}Z`;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : ms;
}

/**
 * Nearest-city label for the Location column: "Dublin, IE"; falls back to
 * rounded coordinates; null when the receiver has neither.
 */
export function locationLabel(receiver) {
  if (!receiver) return null;
  if (receiver.nearestCity) {
    return receiver.nearestCityCountry
      ? `${receiver.nearestCity}, ${receiver.nearestCityCountry}`
      : receiver.nearestCity;
  }
  if (receiver.latitude != null && receiver.longitude != null) {
    return `${Number(receiver.latitude).toFixed(4)}, ${Number(receiver.longitude).toFixed(4)}`;
  }
  return null;
}

/** Coordinates as a tooltip string, or null. */
export function coordinatesLabel(receiver) {
  if (!receiver || receiver.latitude == null || receiver.longitude == null) return null;
  return `${Number(receiver.latitude).toFixed(4)}, ${Number(receiver.longitude).toFixed(4)}`;
}

/**
 * Update state for the Update column.
 * @returns {{state: 'update'|'installer'|'current'|'unknown', latestVersion: string|null}}
 */
export function updateState(meta) {
  if (!hasEverSentStats(meta)) return { state: 'unknown', latestVersion: meta?.latestVersion || null };
  if (meta.installerUpdateRequired) return { state: 'installer', latestVersion: meta.latestVersion || null };
  if (meta.updateAvailable) return { state: 'update', latestVersion: meta.latestVersion || null };
  return { state: 'current', latestVersion: meta.latestVersion || null };
}
